import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { NavController } from '@ionic/angular/standalone';
import { EMPTY, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { PermissionsService } from '../permissions/permissions.service';
import { AuthService } from '../services/auth.service';
import { SessionService } from '../services/session.service';
import { TenantService } from '../services/tenant.service';

const ABP_TENANT_HEADER = 'Abp.TenantId';
const REFRESH_TOKEN_PATH = 'TokenAuth/RefreshToken';
const AUTHENTICATE_PATH = 'TokenAuth/Authenticate';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const sessionService = inject(SessionService);
  const tenantService = inject(TenantService);
  const authService = inject(AuthService);
  const permissionsService = inject(PermissionsService);
  const navCtrl = inject(NavController);

  return next(withAuthHeaders(req, sessionService, tenantService)).pipe(
    catchError((error) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        !req.url.includes(REFRESH_TOKEN_PATH) &&
        // A 401 from the login endpoint itself just means "wrong credentials" — there is no
        // session to expire yet. Without this exclusion, a failed login attempt races the
        // login page's own error banner against this interceptor's refresh-then-redirect
        // flow, which (having no refresh token to try) sends the user to /session-expired a
        // moment later even though they were never logged in.
        !req.url.includes(AUTHENTICATE_PATH)
      ) {
        return authService.tryRefreshToken().pipe(
          switchMap((success) => {
            if (success) {
              return next(withAuthHeaders(req, sessionService, tenantService));
            }
            // Refresh itself failed (or there was no refresh token) — the session cannot be
            // renewed, so this falls through to the classification below carrying the
            // original 401 error, which the classifier treats the same as a 401 retry.
            return throwError(() => error);
          }),
          catchError((retryError) => {
            // Classify by the RETRIED request's own error, not the outer 401 closure
            // variable above — a 401->refresh-ok->retry sequence can fail with any status
            // (e.g. 403 on a Mobile.* permission gate), and only a genuine 401 here means the
            // session truly cannot be re-authenticated. Any other status is the caller's to
            // handle; ending the session on it would be wrong (see design Section 2.1).
            if (retryError instanceof HttpErrorResponse && retryError.status === 401) {
              sessionService.logout();
              permissionsService.clear();
              // navigateRoot (not router.navigate): ion-router-outlet keeps every visited page
              // alive in its navigation stack, so the workflow screen the operator was on stays
              // mounted underneath. A root navigation clears the stack and destroys those
              // component instances, so nothing from the dead session can be re-displayed after
              // the next sign-in.
              void navCtrl.navigateRoot('/session-expired', { animated: false });
              // EMPTY (not throwError): the session is gone and the operator is already being
              // sent to /session-expired, so the calling screen must not also render its own
              // "scan failed" banner for this request — that banner is what used to survive the
              // re-login. Completing without emitting leaves the caller's next/error handlers
              // untouched while still terminating the HTTP subscription.
              return EMPTY;
            }
            return throwError(() => retryError);
          }),
        );
      }
      return throwError(() => error);
    }),
  );
};

function withAuthHeaders(
  req: HttpRequest<unknown>,
  sessionService: SessionService,
  tenantService: TenantService,
): HttpRequest<unknown> {
  let headers = req.headers;

  const token = sessionService.accessToken();
  if (token) {
    headers = headers.set('Authorization', `Bearer ${token}`);
  }

  const tenantId = tenantService.tenantId();
  if (tenantId !== null) {
    headers = headers.set(ABP_TENANT_HEADER, String(tenantId));
  }

  return req.clone({ headers });
}
